import type { BlockyTimeDB } from '@/db/schema'
import { db as defaultDb } from '@/db/schema'
import type { DaySummaryRow, StamperRow } from '@/db/types'
import { getDeviceId } from './syncStateRepository'
import { reorderSiblings, type MoveDirection } from './catalogRepository'

/**
 * Stampers and daily summaries. The only writer of both tables.
 *
 * A stamper is a saved combination — event type, object and comment — applied
 * in one tap. It exists because the two-tap loop is fast but "Gym w/ Ben" is
 * three separate decisions, and typing the same comment every Tuesday is the
 * thing that makes people stop tracking.
 *
 * Daily summaries live here rather than in their own file because they are one
 * free-text field per day with no behaviour beyond upsert, and a repository
 * containing two functions is more structure than that earns.
 */

// --- Stampers ---------------------------------------------------------------

export interface NewStamper {
  name: string
  eventTypeId: string | null
  eventObjectId?: string | null
  comment?: string | null
}

export async function listStampers(db: BlockyTimeDB = defaultDb): Promise<StamperRow[]> {
  const rows = await db.stampers.orderBy('sortOrder').toArray()
  return rows.filter((r) => r.deletedAt === null)
}

export async function createStamper(
  input: NewStamper,
  db: BlockyTimeDB = defaultDb,
): Promise<StamperRow> {
  const name = input.name.trim()
  if (!name) throw new Error('A stamper needs a name')

  const existing = await listStampers(db)
  const row: StamperRow = {
    id: crypto.randomUUID(),
    name,
    eventTypeId: input.eventTypeId,
    eventObjectId: input.eventObjectId ?? null,
    comment: input.comment?.trim() || null,
    sortOrder: existing.reduce((max, s) => Math.max(max, s.sortOrder + 1), 0),
    updatedAt: Date.now(),
    deletedAt: null,
    deviceId: await getDeviceId(db),
    serverSeq: 0,
    _dirty: 1,
  }

  await db.stampers.put(row)
  return row
}

export async function updateStamper(
  id: string,
  patch: Partial<Pick<StamperRow, 'name' | 'eventTypeId' | 'eventObjectId' | 'comment'>>,
  db: BlockyTimeDB = defaultDb,
): Promise<void> {
  const row = await db.stampers.get(id)
  if (!row) return

  await db.stampers.put({
    ...row,
    ...patch,
    updatedAt: Date.now(),
    deviceId: await getDeviceId(db),
    _dirty: 1,
  })
}

export async function deleteStamper(id: string, db: BlockyTimeDB = defaultDb): Promise<void> {
  const row = await db.stampers.get(id)
  if (!row) return

  const now = Date.now()
  await db.stampers.put({
    ...row,
    deletedAt: now,
    updatedAt: now,
    deviceId: await getDeviceId(db),
    _dirty: 1,
  })
}

/** Same dense-renumbering approach as the catalogue, and for the same reason. */
export async function moveStamper(
  id: string,
  direction: MoveDirection,
  db: BlockyTimeDB = defaultDb,
): Promise<boolean> {
  const siblings = await listStampers(db)
  const changes = reorderSiblings(siblings, id, direction)
  if (!changes) return false

  const deviceId = await getDeviceId(db)
  const now = Date.now()

  for (const [rowId, sortOrder] of changes) {
    const row = await db.stampers.get(rowId)
    if (!row) continue
    await db.stampers.put({ ...row, sortOrder, updatedAt: now, deviceId, _dirty: 1 })
  }

  return true
}

// --- Daily summaries --------------------------------------------------------

export async function getDaySummary(
  dayKey: number,
  db: BlockyTimeDB = defaultDb,
): Promise<DaySummaryRow | undefined> {
  const rows = await db.daySummaries.where('dayKey').equals(dayKey).toArray()
  return rows.find((r) => r.deletedAt === null)
}

/** Every day in a range that has a summary. Drives the calendar's note markers. */
export async function daysWithSummaries(
  lo: number,
  hi: number,
  db: BlockyTimeDB = defaultDb,
): Promise<Set<number>> {
  const rows = await db.daySummaries.where('dayKey').between(lo, hi, true, true).toArray()
  const out = new Set<number>()
  for (const row of rows) {
    if (row.deletedAt === null && row.text.trim()) out.add(row.dayKey)
  }
  return out
}

/**
 * Writes a day's note, or tombstones it when cleared.
 *
 * Reuses the existing row's id rather than inserting beside it, for the same
 * reason `entryRepository.upsert` revives a tombstone: the day is the identity,
 * so two devices editing the same day's note resolve by last-write-wins instead
 * of both surviving as duplicates.
 */
export async function setDaySummary(
  dayKey: number,
  text: string,
  db: BlockyTimeDB = defaultDb,
): Promise<void> {
  const trimmed = text.trim()
  const deviceId = await getDeviceId(db)
  const now = Date.now()

  // Includes tombstoned rows, so clearing and re-adding a note keeps one row.
  const all = await db.daySummaries.where('dayKey').equals(dayKey).toArray()
  const existing = all.sort((a, b) => b.updatedAt - a.updatedAt)[0]

  if (!trimmed) {
    if (existing && existing.deletedAt === null) {
      await db.daySummaries.put({
        ...existing,
        text: '',
        deletedAt: now,
        updatedAt: now,
        deviceId,
        _dirty: 1,
      })
    }
    return
  }

  const row: DaySummaryRow = existing
    ? { ...existing, text: trimmed, deletedAt: null, updatedAt: now, deviceId, _dirty: 1 }
    : {
        id: crypto.randomUUID(),
        dayKey,
        text: trimmed,
        updatedAt: now,
        deletedAt: null,
        deviceId,
        serverSeq: 0,
        _dirty: 1,
      }

  await db.daySummaries.put(row)
}
